import { t } from '@/i18n';
import { REFRESH_KEY, useSession } from './session';
import type { TokenPair } from './types';

/**
 * `VITE_API_URL` е ПЪЛНАТА основа на API-то, включително префикса `api`
 * (сървърът го слага глобално — виж apps/api/src/main.ts). Същата конвенция
 * ползват и шофьорското приложение, и клиентският портал; не я разминавай,
 * защото резултатът е тих `/api/api/...`.
 */
const RAW_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api';
export const API_BASE = RAW_BASE.replace(/\/+$/, '');
export const API_ORIGIN = API_BASE.replace(/\/api$/, '');

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
    requestId?: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string[]> | undefined;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    fields?: Record<string, string[]>,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.requestId = requestId;
  }

  /** Мрежата пада по-често от сървъра — роумингът в офиса е кабел, но VPN-ът не е. */
  static network(): ApiError {
    return new ApiError(0, 'NETWORK', t.errors.network);
  }
}

function messageForStatus(status: number): string {
  if (status === 401) return t.auth.sessionExpired;
  if (status === 403) return t.errors.unauthorized;
  if (status === 404) return t.errors.notFound;
  if (status === 409) return t.errors.conflict;
  if (status === 422 || status === 400) return t.errors.validation;
  if (status >= 500) return t.errors.server;
  return t.errors.unknown;
}

export type QueryValue = string | number | boolean | null | undefined | Array<string | number>;

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  // Базата може да е относителна („/api“, когато нещо отпред прокси-ва към API-то).
  // `new URL` иска абсолютен адрес и хвърля синхронно, а горният слой превежда всяко
  // хвърляне като „няма връзка със сървъра“ — тоест грешна настройка се маскира като
  // мрежов проблем и се търси на грешното място. Затова подаваме origin-а за база.
  const url = new URL(
    `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`,
    window.location.origin,
  );
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) {
        for (const v of value) url.searchParams.append(key, String(v));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

/** ─── Подновяване на токена ───────────────────────────────────────────── */

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Refresh токенът е ЕДИН за всички раздели (стои в localStorage), а всяко
 * подновяване го сменя. Два отворени раздела, които подновяват сами, карат
 * сървъра да види стария токен втори път — „кражба" — и той прекратява всички
 * сесии. Точно така диспечерът беше изхвърлян.
 *
 * Затова: подновяването минава под обща за разделите ключалка, и вътре в нея
 * токенът се чете наново от localStorage — ако друг раздел току-що го е
 * подновил, се ползва новият.
 */
function storedRefreshToken(): string | null {
  try {
    const raw = localStorage.getItem(REFRESH_KEY);
    return raw ? (JSON.parse(raw) as string) : null;
  } catch {
    return null;
  }
}

function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  /* Web Locks API — обща за разделите от един произход. Където я няма (стари браузъри), без ключалка. */
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('kmdx.dispatch.refresh', fn) as Promise<T>;
  }
  return fn();
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  if (!storedRefreshToken() && !useSession.getState().refreshToken) return null;

  refreshInFlight = withRefreshLock(async () => {
    try {
      const post = (refreshToken: string) =>
        fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      const token = storedRefreshToken() ?? useSession.getState().refreshToken;
      if (!token) return null;
      let response = await post(token);
      /** Друг раздел е подновил секунди по-рано — опит с неговия нов токен. */
      if (response.status === 401) {
        const code = ((await response.clone().json().catch(() => null)) as { error?: { code?: string } } | null)?.error?.code;
        const newer = storedRefreshToken();
        if (code === 'REFRESH_RACE' && newer && newer !== token) response = await post(newer);
      }
      if (!response.ok) {
        /** Само отказ на сесията я прекратява — сървърна грешка не изхвърля човека. */
        if (response.status === 401) useSession.getState().clear();
        return null;
      }
      const pair = (await response.json()) as TokenPair;
      useSession.getState().setTokens({
        accessToken: pair.accessToken,
        expiresIn: pair.expiresIn,
        refreshToken: pair.refreshToken,
      });
      return pair.accessToken;
    } catch {
      // Мрежова грешка при подновяване не изхвърля сесията — токенът може още да е валиден.
      return null;
    } finally {
      refreshInFlight = null;
    }
  });

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Пропуска токена — само за вход и подновяване */
  anonymous?: boolean;
}

async function parseError(response: Response): Promise<ApiError> {
  let payload: ErrorEnvelope | null = null;
  try {
    payload = (await response.json()) as ErrorEnvelope;
  } catch {
    payload = null;
  }
  const envelope = payload?.error;
  return new ApiError(
    response.status,
    envelope?.code ?? `HTTP_${response.status}`,
    envelope?.message ?? messageForStatus(response.status),
    envelope?.fields,
    envelope?.requestId,
  );
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, anonymous = false } = options;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers.authorization = `Bearer ${token}`;
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (signal) init.signal = signal;
    return fetch(buildUrl(path, query), init);
  };

  let token = anonymous ? null : useSession.getState().accessToken;
  /**
   * След презареждане access токенът го няма (стои само в паметта). Подновява
   * се ПРЕДИ заявката, вместо всяка заявка на страницата първо да получи 401.
   */
  if (!anonymous && !token && useSession.getState().refreshToken) token = await refreshAccessToken();
  let response: Response;
  try {
    response = await send(token);
  } catch {
    throw ApiError.network();
  }

  // Един опит за подновяване, после сесията се смята за приключила.
  if (response.status === 401 && !anonymous) {
    token = await refreshAccessToken();
    if (token) {
      try {
        response = await send(token);
      } catch {
        throw ApiError.network();
      }
    } else {
      useSession.getState().clear();
      throw new ApiError(401, 'SESSION_EXPIRED', t.auth.sessionExpired);
    }
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await response.text()) as unknown as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, signal ? { query, signal } : { query }),
  post: <T>(path: string, body?: unknown, query?: Record<string, QueryValue>) =>
    request<T>(path, { method: 'POST', body, query }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Токен за потоци (SSE), които се четат с fetch, за да носят заглавка. */
export async function currentAccessToken(): Promise<string | null> {
  const { accessToken, accessExpiresAt } = useSession.getState();
  if (accessToken && accessExpiresAt && accessExpiresAt - Date.now() > 15_000) return accessToken;
  const refreshed = await refreshAccessToken();
  return refreshed ?? accessToken;
}
